import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  Plus,
  Eye,
  Edit2,
  Trash2,
  Package,
  Tags,
  DollarSign,
  CheckCircle,
  XCircle,
  Download,
  Upload,
  FolderOpen,
  X,
  RefreshCw,
  FileText,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import apiService from "../services/api";
import ProductForm from "../components/ProductForm";
import Modal from "../components/ui/Modal";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { API_BASE_URL } from "../config/api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function Products() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [viewProduct, setViewProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("active"); // 'all', 'active', 'inactive'

  // Pagination and Sorting State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [clearBeforeImport, setClearBeforeImport] = useState(false);
  const [clearOptions, setClearOptions] = useState({
    products: true,
    categories: false,
  });
  const [activeResultTab, setActiveResultTab] = useState("success"); // 'success' or 'errors'
  const [importProgress, setImportProgress] = useState({
    total: 0,
    current: 0,
    success: 0,
    created: 0,
    updated: 0,
    skipped: 0,
    errors: 0,
    errorDetails: [],
    successDetails: [],
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [overallStats, setOverallStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    categories: 0,
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchOverallStats();
  }, []);

  // Re-fetch when status filter changes
  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (selectedCategory) params.categoryId = selectedCategory;

      // Pass status filter to backend
      if (selectedStatus === "all") {
        params.isActive = "all";
      } else if (selectedStatus === "inactive") {
        params.isActive = "false";
      } else {
        params.isActive = "true"; // default: active only
      }

      const data = await apiService.getProducts(params);
      setProducts(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  // Filter products based on search and category (client-side)
  // Status filtering is now done server-side
  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesSearch =
        !searchTerm ||
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.itemCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        !selectedCategory ||
        product.primaryCategory?.id?.toString() === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [products, searchTerm, selectedCategory]);

  // Display stats - use overallStats for accurate counts
  const stats = overallStats;

  const hasActiveFilters =
    searchTerm || selectedCategory || selectedStatus !== "active";

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("");
    setSelectedStatus("active");
  };

  // Sorting Logic
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedProducts = useMemo(() => {
    let sortableItems = [...filteredProducts];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle nested properties (e.g., primaryCategory.name)
        if (sortConfig.key.includes(".")) {
          const keys = sortConfig.key.split(".");
          aValue = keys.reduce(
            (obj, key) => (obj && obj[key] !== "undefined" ? obj[key] : ""),
            a,
          );
          bValue = keys.reduce(
            (obj, key) => (obj && obj[key] !== "undefined" ? obj[key] : ""),
            b,
          );
        }

        // Special handling for specific fields
        if (sortConfig.key === "category") {
          aValue = a.categoryPath || a.primaryCategory?.name || "";
          bValue = b.categoryPath || b.primaryCategory?.name || "";
        }

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) aValue = "";
        if (bValue === null || bValue === undefined) bValue = "";

        // String comparison
        if (typeof aValue === "string") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredProducts, sortConfig]);

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedProducts = sortedProducts.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  const fetchCategories = async () => {
    try {
      const data = await apiService.getProductCategories();
      setCategories(data);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  };

  const fetchOverallStats = async () => {
    try {
      // Fetch all products to get accurate counts
      const allProducts = await apiService.getProducts({ isActive: "all" });
      setOverallStats({
        total: allProducts.length,
        active: allProducts.filter((p) => p.isActive !== false).length,
        inactive: allProducts.filter((p) => p.isActive === false).length,
        categories: [
          ...new Set(allProducts.map((p) => p.primaryCategory?.id)),
        ].filter(Boolean).length,
      });
    } catch (err) {
      console.error("Failed to fetch overall stats:", err);
    }
  };

  const handleSearch = () => {
    fetchProducts();
  };

  const handleAddProduct = () => {
    navigate("/admin/products/add");
  };

  // Export products to PDF
  const exportToPDF = async () => {
    try {
      // Fetch all products for export (regardless of current filter)
      const allProducts = await apiService.getProducts({ isActive: "all" });

      // Sort products by item code
      const sortedForExport = [...allProducts].sort((a, b) => {
        const aCode = a.itemCode || "";
        const bCode = b.itemCode || "";
        return aCode.localeCompare(bCode);
      });

      const doc = new jsPDF();

      // Add title
      doc.setFontSize(18);
      doc.setFont(undefined, "bold");
      doc.text("Products List - All Products", 14, 22);

      // Add date and filter info
      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      const now = new Date();
      doc.text(`Generated: ${now.toLocaleString()}`, 14, 30);

      // Add summary stats
      const activeCount = allProducts.filter(
        (p) => p.isActive !== false,
      ).length;
      const inactiveCount = allProducts.filter(
        (p) => p.isActive === false,
      ).length;
      doc.text(
        `Total: ${allProducts.length} | Active: ${activeCount} | Inactive: ${inactiveCount}`,
        14,
        36,
      );

      // Prepare table data
      const tableData = sortedForExport.map((product) => [
        product.name || "-",
        product.itemCode || "-",
        product.skus?.length || 0,
        product.primaryCategory?.name || product.categoryPath || "-",
        product.costPrice
          ? `₹${parseFloat(product.costPrice).toFixed(2)}`
          : "-",
        product.sellingPrice
          ? `₹${parseFloat(product.sellingPrice).toFixed(2)}`
          : "-",
        product.isActive ? "Active" : "Inactive",
      ]);

      // Add table
      autoTable(doc, {
        startY: 42,
        head: [
          [
            "Product Name",
            "Item Code",
            "SKUs",
            "Category",
            "Cost Price",
            "Selling Price",
            "Status",
          ],
        ],
        body: tableData,
        theme: "striped",
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: 255,
          fontStyle: "bold",
          halign: "left",
        },
        styles: {
          fontSize: 9,
          cellPadding: 3,
        },
        columnStyles: {
          0: { cellWidth: 45 },
          1: { cellWidth: 25 },
          2: { cellWidth: 15, halign: "center" },
          3: { cellWidth: 30 },
          4: { cellWidth: 25, halign: "right" },
          5: { cellWidth: 25, halign: "right" },
          6: { cellWidth: 20, halign: "center" },
        },
        margin: { left: 14, right: 14 },
      });

      // Add footer with total count
      const finalY = doc.lastAutoTable.finalY || 42;
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.text(`Total Products: ${allProducts.length}`, 14, finalY + 10);

      // Save the PDF
      const fileName = `products_all_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}.pdf`;
      doc.save(fileName);

      toast.success(
        `PDF exported successfully! (${allProducts.length} products)`,
      );
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Failed to export PDF");
    }
  };

  const handleEditProduct = (product) => {
    navigate(`/admin/products/edit/${product.id}`);
  };

  const handleViewProduct = (product) => {
    setViewProduct(product);
    setShowViewModal(true);
  };

  const handleSaveProduct = async (data) => {
    try {
      if (editingProduct) {
        await apiService.updateProduct(editingProduct.id, data);
        toast.success("Product updated successfully");
      } else {
        await apiService.createProduct(data);
        toast.success("Product created successfully");
      }
      setShowProductForm(false);
      setEditingProduct(null);
      fetchProducts();
      fetchOverallStats();
    } catch (error) {
      toast.error(error.message || "Failed to save product");
    }
  };

  const handleDeleteProduct = (product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await apiService.deleteProduct(productToDelete.id);
      toast.success("Product deleted successfully");
      setShowDeleteModal(false);
      setProductToDelete(null);
      fetchProducts();
      fetchOverallStats();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setProductToDelete(null);
  };

  // Excel Import Functions
  const downloadTemplate = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/products/template/download`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to download template");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "products_import_template.xlsx";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("Template downloaded successfully");
    } catch (error) {
      console.error("Template download error:", error);
      toast.error("Failed to download template");
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel"
      ) {
        setImportFile(file);
      } else {
        toast.error("Please select a valid Excel file (.xlsx or .xls)");
      }
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error("Please select a file to import");
      return;
    }

    setImporting(true);
    setImportProgress({
      total: 0,
      current: 0,
      success: 0,
      created: 0,
      updated: 0,
      skipped: 0,
      errors: 0,
      errorDetails: [],
      successDetails: [],
    });

    try {
      // Step 1: Clear existing data if requested
      if (clearBeforeImport) {
        try {
          const clearParams = new URLSearchParams();
          if (clearOptions.products) clearParams.append("products", "true");
          if (clearOptions.categories) clearParams.append("categories", "true");

          const clearResponse = await fetch(
            `${API_BASE_URL}/api/products/clear-data?${clearParams.toString()}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            },
          );

          const clearResult = await clearResponse.json();

          if (!clearResponse.ok) {
            throw new Error(clearResult.error || "Failed to clear data");
          }

          toast.success(
            `Cleared: ${clearResult.productsDeleted || 0} products, ${clearResult.skusDeleted || 0} SKUs${clearOptions.categories ? `, ${clearResult.categoriesDeleted || 0} categories` : ""}`,
          );
        } catch (clearError) {
          console.error("Clear data error:", clearError);
          toast.error(`Failed to clear data: ${clearError.message}`);
          setImporting(false);
          return;
        }
      }

      // Step 2: Proceed with import
      const formData = new FormData();
      formData.append("file", importFile);

      const response = await fetch(
        `${API_BASE_URL}/api/products/import?replaceExisting=${replaceExisting}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formData,
        },
      );

      const result = await response.json();

      console.log("Import response:", { status: response.status, result });
      console.log("Error details received:", result.errorDetails);
      console.log("Success details received:", result.successDetails);

      if (response.ok) {
        const successMsg =
          result.errors === 0
            ? `✅ All ${result.success} products processed successfully!${result.skipped > 0 ? ` (${result.created} created, ${result.skipped} skipped as duplicates)` : ""}`
            : `Import completed: ${result.created} created, ${result.updated || 0} updated, ${result.skipped || 0} skipped, ${result.errors} failed`;

        if (result.errors === 0) {
          toast.success(successMsg);
        } else {
          toast(successMsg, { icon: "⚠️" });
        }

        setImportProgress({
          total: result.total,
          current: result.total,
          success: result.success,
          created: result.created || 0,
          updated: result.updated || 0,
          skipped: result.skipped || 0,
          errors: result.errors,
          errorDetails: result.errorDetails || [],
          successDetails: result.successDetails || [],
        });

        console.log(
          "State set with errorDetails count:",
          result.errorDetails?.length || 0,
        );
        console.log(
          "State set with successDetails count:",
          result.successDetails?.length || 0,
        );

        // Default to errors tab if there are any errors, otherwise success tab
        setActiveResultTab(result.errors > 0 ? "errors" : "success");
        console.log(
          "Active tab set to:",
          result.errors > 0 ? "errors" : "success",
        );

        fetchProducts(); // Refresh the products list
        fetchOverallStats(); // Refresh stats
      } else {
        console.error("Import failed with status:", response.status, result);
        toast.error(result.error || result.message || "Import failed");
        setImportProgress({
          total: 0,
          current: 0,
          success: 0,
          created: 0,
          updated: 0,
          skipped: 0,
          errors: 1,
          errorDetails: [
            {
              row: 0,
              productName: "N/A",
              itemCode: "N/A",
              sku: "N/A",
              error: result.error || result.message || "Import failed",
            },
          ],
          successDetails: [],
        });
        setActiveResultTab("errors");
      }
    } catch (error) {
      console.error("Import error:", error);
      const errorMsg =
        error.message ||
        "Import failed. Please check your file format and try again.";
      toast.error(errorMsg);
      setImportProgress({
        total: 0,
        current: 0,
        success: 0,
        created: 0,
        updated: 0,
        skipped: 0,
        errors: 1,
        errorDetails: [
          {
            row: 0,
            productName: "N/A",
            itemCode: "N/A",
            sku: "N/A",
            error: errorMsg,
          },
        ],
        successDetails: [],
      });
      setActiveResultTab("errors");
    } finally {
      setImporting(false);
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    try {
      await apiService.addToCart(productId, quantity);
      toast.success("Added to cart");
    } catch (error) {
      if (error.message && error.message.includes("Insufficient inventory")) {
        toast.error("Insufficient inventory available for this product");
      } else {
        toast.error("Failed to add to cart");
      }
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-medium">{error}</p>
          <button
            onClick={fetchProducts}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Products
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage your product catalog
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={exportToPDF}
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <FileText className="w-4 h-4" />
              Export PDF
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Upload className="w-4 h-4" />
              Import
            </button>
            <button
              onClick={handleAddProduct}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/25 transition-all duration-200"
            >
              <Plus className="w-4 h-4" />
              Add Product
            </button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {overallStats.total}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Total Products
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
              <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {overallStats.active}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 dark:bg-red-900/30 rounded-lg">
              <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {overallStats.inactive}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Inactive
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
              <Tags className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {overallStats.categories}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Categories
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-medium transition-colors ${
                showFilters || hasActiveFilters
                  ? "border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                  : "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="flex items-center justify-center w-5 h-5 bg-blue-600 text-white text-xs rounded-full">
                  {(searchTerm ? 1 : 0) +
                    (selectedCategory ? 1 : 0) +
                    (selectedStatus !== "active" ? 1 : 0)}
                </span>
              )}
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-red-600 dark:text-red-400 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>

          {/* Collapsible Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                    Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    <option value="">All Categories</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id.toString()}>
                        {cat.path || cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                    Status
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    <option value="active">Active Only</option>
                    <option value="all">All Products</option>
                    <option value="inactive">Inactive Only</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Products Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center gap-1">
                    Product
                    {sortConfig.key === "name" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp size={14} />
                      ) : (
                        <ArrowDown size={14} />
                      )
                    ) : (
                      <ArrowUpDown size={14} className="text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("itemCode")}
                >
                  <div className="flex items-center gap-1">
                    Item Code
                    {sortConfig.key === "itemCode" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp size={14} />
                      ) : (
                        <ArrowDown size={14} />
                      )
                    ) : (
                      <ArrowUpDown size={14} className="text-gray-400" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SKUs
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("category")}
                >
                  <div className="flex items-center gap-1">
                    Category
                    {sortConfig.key === "category" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp size={14} />
                      ) : (
                        <ArrowDown size={14} />
                      )
                    ) : (
                      <ArrowUpDown size={14} className="text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("costPrice")}
                >
                  <div className="flex items-center gap-1">
                    Cost Price
                    {sortConfig.key === "costPrice" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp size={14} />
                      ) : (
                        <ArrowDown size={14} />
                      )
                    ) : (
                      <ArrowUpDown size={14} className="text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("sellingPrice")}
                >
                  <div className="flex items-center gap-1">
                    Selling Price
                    {sortConfig.key === "sellingPrice" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp size={14} />
                      ) : (
                        <ArrowDown size={14} />
                      )
                    ) : (
                      <ArrowUpDown size={14} className="text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("isActive")}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {sortConfig.key === "isActive" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp size={14} />
                      ) : (
                        <ArrowDown size={14} />
                      )
                    ) : (
                      <ArrowUpDown size={14} className="text-gray-400" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedProducts.map((product) => (
                <tr
                  key={product.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        {(product.imageUrls && product.imageUrls.length > 0) ||
                        product.imageUrl ? (
                          <img
                            src={
                              product.imageUrls && product.imageUrls.length > 0
                                ? product.imageUrls[0]
                                : product.imageUrl
                            }
                            alt={product.name}
                            className="h-10 w-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                            <Package className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {product.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                          {product.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {product.itemCode}
                      </div>
                      {product.skus && product.skus.length > 0 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {product.skus.length} SKU(s)
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.skus && product.skus.length > 0 ? (
                      <div className="space-y-1">
                        {product.skus.slice(0, 2).map((sku, index) => (
                          <div key={sku.id} className="text-xs">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {sku.sku}
                            </div>
                            <div className="text-gray-500 dark:text-gray-400">
                              {sku.barcode}
                            </div>
                          </div>
                        ))}
                        {product.skus.length > 2 && (
                          <div className="text-xs text-blue-600 dark:text-blue-400">
                            +{product.skus.length - 2} more
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {product.categoryPath ||
                      product.primaryCategory?.name ||
                      "Uncategorized"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    ₹{product.costPrice}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600 dark:text-blue-400">
                    ₹{product.sellingPrice}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        product.isActive !== false
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800"
                          : "bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                          product.isActive !== false
                            ? "bg-emerald-500"
                            : "bg-red-500"
                        }`}
                      />
                      {product.isActive !== false ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleViewProduct(product)}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => handleEditProduct(product)}
                            className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(product)}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing{" "}
            <span className="font-medium text-gray-900 dark:text-white">
              {sortedProducts.length === 0 ? 0 : indexOfFirstItem + 1}
            </span>{" "}
            to{" "}
            <span className="font-medium text-gray-900 dark:text-white">
              {Math.min(indexOfLastItem, sortedProducts.length)}
            </span>{" "}
            of{" "}
            <span className="font-medium text-gray-900 dark:text-white">
              {sortedProducts.length}
            </span>{" "}
            products
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg transition-colors ${
                  currentPage === 1
                    ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => paginate(pageNum)}
                    className={`min-w-[36px] h-9 px-3 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg transition-colors ${
                  currentPage === totalPages
                    ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Empty State */}
      {paginatedProducts.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full">
              <Package className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No products found
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {hasActiveFilters
              ? "Try adjusting your search or filter criteria"
              : "Get started by adding your first product"}
          </p>
          {hasActiveFilters ? (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-2 px-4 py-2 text-blue-600 dark:text-blue-400 text-sm font-medium hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              Clear all filters
            </button>
          ) : (
            isAdmin && (
              <button
                onClick={handleAddProduct}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/25 transition-all duration-200"
              >
                <Plus className="w-4 h-4" />
                Add Product
              </button>
            )
          )}
        </div>
      )}

      {/* Product Form Modal */}
      <ProductForm
        open={showProductForm}
        onClose={() => {
          setShowProductForm(false);
          setEditingProduct(null);
        }}
        onSave={handleSaveProduct}
        initialData={editingProduct}
      />

      {/* View Product Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Product Details"
        size="6xl"
      >
        {viewProduct && (
          <div className="space-y-8">
            {/* Header with Product Overview */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span className="text-blue-600 text-lg">📦</span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        {viewProduct.name}
                      </h2>
                      <p className="text-gray-600">{viewProduct.itemCode}</p>
                    </div>
                  </div>
                  <p className="text-gray-700 mt-2">
                    {viewProduct.description}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-blue-600">
                    ₹{viewProduct.sellingPrice}
                  </div>
                  <div className="text-sm text-gray-500">Selling Price</div>
                  <div className="text-lg font-medium text-gray-700 mt-1">
                    ₹{viewProduct.costPrice}
                  </div>
                  <div className="text-sm text-gray-500">Cost Price</div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="text-sm text-gray-500">SKUs</div>
                  <div className="text-xl font-bold text-gray-900">
                    {viewProduct.skus?.length || 0}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="text-sm text-gray-500">GST</div>
                  <div className="text-xl font-bold text-gray-900">
                    {viewProduct.gstPercent}%
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="text-sm text-gray-500">Brand</div>
                  <div className="text-xl font-bold text-gray-900">
                    {viewProduct.brand || "N/A"}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="text-sm text-gray-500">Status</div>
                  <div
                    className={`text-xl font-bold ${
                      viewProduct.isActive !== false
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {viewProduct.isActive !== false ? "Active" : "Inactive"}
                  </div>
                </div>
              </div>
            </div>

            {/* SKU Comparison Section */}
            {viewProduct.skus && viewProduct.skus.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <span className="mr-2">🔍</span>
                    SKU Variants Comparison
                    <span className="ml-2 text-sm font-normal text-gray-600">
                      ({viewProduct.skus.length} variants)
                    </span>
                  </h3>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          SKU Code
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Variant Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Barcode
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cost Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Selling Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Weight
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Dimensions
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {viewProduct.skus.map((sku, index) => (
                        <tr
                          key={sku.id}
                          className={`hover:bg-gray-50 ${
                            index % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                                <span className="text-blue-600 text-sm font-bold">
                                  {index + 1}
                                </span>
                              </div>
                              <div>
                                <div className="text-sm font-bold text-gray-900">
                                  {sku.sku}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Variant {index + 1}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">
                              {sku.name || "N/A"}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 font-mono">
                              {sku.barcode}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">
                              ₹{sku.costPrice || viewProduct.costPrice}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-bold text-blue-600">
                              ₹{sku.sellingPrice || viewProduct.sellingPrice}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {sku.weight ? `${sku.weight} kg` : "N/A"}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {sku.dimensions || "N/A"}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div
                              className="text-sm text-gray-600 max-w-xs truncate"
                              title={sku.description}
                            >
                              {sku.description || "No description"}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Product Information Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Basic Information */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">📋</span>
                  Basic Information
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Category
                      </label>
                      <p className="text-gray-900 font-medium">
                        {viewProduct.categoryPath ||
                          viewProduct.primaryCategory?.name ||
                          "Uncategorized"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Brand
                      </label>
                      <p className="text-gray-900 font-medium">
                        {viewProduct.brand || "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Unit of Measure
                      </label>
                      <p className="text-gray-900 font-medium">
                        {viewProduct.unitOfMeasure || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        GST Percentage
                      </label>
                      <p className="text-gray-900 font-medium">
                        {viewProduct.gstPercent}%
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Description
                    </label>
                    <p className="text-gray-900">
                      {viewProduct.description || "No description available"}
                    </p>
                  </div>
                </div>
              </div>

              {/* B2B Features */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">🏢</span>
                  B2B Features
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Min Order Qty
                      </label>
                      <p className="text-gray-900 font-medium">
                        {viewProduct.minimumOrderQuantity || 1}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Max Order Qty
                      </label>
                      <p className="text-gray-900 font-medium">
                        {viewProduct.maximumOrderQuantity || "No limit"}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Lead Time
                      </label>
                      <p className="text-gray-900 font-medium">
                        {viewProduct.leadTime
                          ? `${viewProduct.leadTime} days`
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Reorder Point
                      </label>
                      <p className="text-gray-900 font-medium">
                        {viewProduct.reorderPoint || "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={viewProduct.isActive !== false}
                        disabled
                        className="rounded border-gray-300 bg-gray-100"
                      />
                      <label className="ml-2 text-sm font-medium text-gray-700">
                        Active Product
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={viewProduct.isBulkPricingEnabled || false}
                        disabled
                        className="rounded border-gray-300 bg-gray-100"
                      />
                      <label className="ml-2 text-sm font-medium text-gray-700">
                        Bulk Pricing
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Industrial Information */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">🏭</span>
                  Industrial Information
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        HS Code
                      </label>
                      <p className="text-gray-900 font-medium">
                        {viewProduct.hsCode || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Country of Origin
                      </label>
                      <p className="text-gray-900 font-medium">
                        {viewProduct.countryOfOrigin || "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Weight
                      </label>
                      <p className="text-gray-900 font-medium">
                        {viewProduct.weight
                          ? `${viewProduct.weight} kg`
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Dimensions
                      </label>
                      <p className="text-gray-900 font-medium">
                        {viewProduct.dimensions || "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={viewProduct.isHazmat || false}
                      disabled
                      className="rounded border-gray-300 bg-gray-100"
                    />
                    <label className="ml-2 text-sm font-medium text-gray-700">
                      HAZMAT (Hazardous Material)
                    </label>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">📝</span>
                  Additional Information
                </h3>
                <div className="space-y-4">
                  {viewProduct.specialInstructions && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Special Instructions
                      </label>
                      <p className="text-gray-900">
                        {viewProduct.specialInstructions}
                      </p>
                    </div>
                  )}
                  {viewProduct.packing && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Packing
                      </label>
                      <p className="text-gray-900">{viewProduct.packing}</p>
                    </div>
                  )}
                  {viewProduct.discountEligibility && (
                    <div className="flex items-center text-green-700">
                      <span className="mr-2">✅</span>
                      <span className="text-sm font-medium">
                        Eligible for discounts
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bulk Pricing Section */}
            {viewProduct.isBulkPricingEnabled &&
              viewProduct.bulkPricing &&
              viewProduct.bulkPricing.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">💰</span>
                    Bulk Pricing Tiers
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Tier
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Min Qty
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Max Qty
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Price
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Discount
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {viewProduct.bulkPricing.map((tier, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                <span className="text-blue-600 text-sm font-bold">
                                  {index + 1}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {tier.minQuantity}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {tier.maxQuantity || "∞"}
                            </td>
                            <td className="px-4 py-3 text-sm font-bold text-blue-600">
                              ₹{tier.price}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {tier.discount || 0}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            {/* JSON Data Sections */}
            {(viewProduct.specifications ||
              viewProduct.packagingOptions ||
              viewProduct.industryFeatures) && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">⚙️</span>
                  Technical Data
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {viewProduct.specifications && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Specifications
                      </h4>
                      <pre className="text-xs bg-gray-50 p-3 rounded-lg border overflow-auto max-h-32">
                        {typeof viewProduct.specifications === "string"
                          ? viewProduct.specifications
                          : JSON.stringify(viewProduct.specifications, null, 2)}
                      </pre>
                    </div>
                  )}
                  {viewProduct.packagingOptions && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Packaging Options
                      </h4>
                      <pre className="text-xs bg-gray-50 p-3 rounded-lg border overflow-auto max-h-32">
                        {typeof viewProduct.packagingOptions === "string"
                          ? viewProduct.packagingOptions
                          : JSON.stringify(
                              viewProduct.packagingOptions,
                              null,
                              2,
                            )}
                      </pre>
                    </div>
                  )}
                  {viewProduct.industryFeatures && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Industry Features
                      </h4>
                      <pre className="text-xs bg-gray-50 p-3 rounded-lg border overflow-auto max-h-32">
                        {typeof viewProduct.industryFeatures === "string"
                          ? viewProduct.industryFeatures
                          : JSON.stringify(
                              viewProduct.industryFeatures,
                              null,
                              2,
                            )}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end pt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowViewModal(false)}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Import Products Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => {
          setShowImportModal(false);
          setReplaceExisting(false);
          setClearBeforeImport(false);
          setClearOptions({ products: true, categories: false });
        }}
        title="Import Products from Excel"
        size="xl"
      >
        <div className="space-y-6">
          {/* Step 1: Download Template */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                  Download Template
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">
                  Download the Excel template with sample data and fill in your
                  products
                </p>
                <Button
                  onClick={downloadTemplate}
                  variant="outline"
                  size="sm"
                  className="bg-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Excel Template
                </Button>
              </div>
            </div>
          </div>

          {/* Step 2: Upload File */}
          <div className="bg-white dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-semibold">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                  Select Excel File
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">
                  Choose the filled Excel file to import
                </p>

                {!importFile ? (
                  <button
                    onClick={() =>
                      fileInputRef.current && fileInputRef.current.click()
                    }
                    className="w-full py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all group"
                  >
                    <div className="flex flex-col items-center">
                      <Upload className="w-12 h-12 text-gray-400 group-hover:text-blue-500 mb-2" />
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-blue-600">
                        Click to select file
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Excel files (.xlsx, .xls)
                      </p>
                    </div>
                  </button>
                ) : (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-600 rounded-lg">
                          <FolderOpen className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                            {importFile.name}
                          </p>
                          <p className="text-xs text-blue-600 dark:text-blue-300">
                            {(importFile.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setImportFile(null);
                          fileInputRef.current.value = "";
                        }}
                        className="text-red-600 hover:text-red-700 p-2"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}
                <input
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={handleFileUpload}
                  ref={fileInputRef}
                  className="hidden"
                />
              </div>
            </div>
          </div>

          {/* Step 3: Import Options */}
          <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-semibold">
                3
              </div>
              <div className="flex-1 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Import Options
                </h3>

                {/* Clear Before Import Option */}
                <div className="bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={clearBeforeImport}
                      onChange={(e) => setClearBeforeImport(e.target.checked)}
                      className="mt-0.5 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                    />
                    <div className="ml-3 flex-1">
                      <span className="text-sm font-medium text-red-800 dark:text-red-400">
                        🗑️ Clear existing data before import
                      </span>
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        ⚠️ Will permanently delete existing data before
                        importing
                      </p>

                      {clearBeforeImport && (
                        <div className="mt-3 space-y-2 border-l-2 border-red-300 pl-3">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={clearOptions.products}
                              onChange={(e) =>
                                setClearOptions({
                                  ...clearOptions,
                                  products: e.target.checked,
                                })
                              }
                              className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                            />
                            <span className="ml-2 text-xs text-red-700 dark:text-red-400">
                              Delete all products and SKUs
                            </span>
                          </label>
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={clearOptions.categories}
                              onChange={(e) =>
                                setClearOptions({
                                  ...clearOptions,
                                  categories: e.target.checked,
                                })
                              }
                              className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                            />
                            <span className="ml-2 text-xs text-red-700 dark:text-red-400">
                              Delete all categories
                            </span>
                          </label>
                        </div>
                      )}
                    </div>
                  </label>
                </div>

                {/* Replace Existing Option */}
                <div className="bg-white dark:bg-gray-800 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={replaceExisting}
                      onChange={(e) => setReplaceExisting(e.target.checked)}
                      disabled={clearBeforeImport}
                      className="mt-0.5 h-4 w-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <div className="ml-3 flex-1">
                      <span
                        className={`text-sm font-medium ${clearBeforeImport ? "text-gray-400" : "text-yellow-800 dark:text-yellow-400"}`}
                      >
                        🔄 Replace existing products
                      </span>
                      <p
                        className={`text-xs mt-1 ${clearBeforeImport ? "text-gray-400" : "text-yellow-600 dark:text-yellow-400"}`}
                      >
                        {clearBeforeImport
                          ? "Disabled when clearing data - all data will be fresh"
                          : "Update products with duplicate SKUs/barcodes instead of skipping"}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Import Button */}
          {!importing && importProgress.total === 0 && (
            <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setReplaceExisting(false);
                  setClearBeforeImport(false);
                  setClearOptions({ products: true, categories: false });
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                variant="primary"
                disabled={!importFile}
                className="min-w-[140px]"
              >
                {!importFile ? (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Select File First
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Start Import
                  </>
                )}
              </Button>
            </div>
          )}

          {importing && (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="lg" />
              <p className="ml-4 text-lg text-gray-700">
                Importing products...
              </p>
            </div>
          )}

          {importProgress.total > 0 && !importing && (
            <div className="space-y-4 mt-4">
              {/* Summary */}
              <div
                className={`border rounded-md p-4 ${
                  importProgress.errors === 0
                    ? "bg-green-50 border-green-200"
                    : importProgress.success === 0
                      ? "bg-red-50 border-red-200"
                      : "bg-yellow-50 border-yellow-200"
                }`}
              >
                <h4
                  className={`text-sm font-medium mb-2 ${
                    importProgress.errors === 0
                      ? "text-green-800"
                      : importProgress.success === 0
                        ? "text-red-800"
                        : "text-yellow-800"
                  }`}
                >
                  Import Summary
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Total Rows</p>
                    <p className="text-lg font-semibold">
                      {importProgress.total}
                    </p>
                  </div>
                  <div>
                    <p className="text-blue-600">Created</p>
                    <p className="text-lg font-semibold text-blue-700">
                      {importProgress.created || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-purple-600">Updated</p>
                    <p className="text-lg font-semibold text-purple-700">
                      {importProgress.updated || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-orange-600">Skipped</p>
                    <p className="text-lg font-semibold text-orange-700">
                      {importProgress.skipped || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-red-600">Errors</p>
                    <p className="text-lg font-semibold text-red-700">
                      {importProgress.errors}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tabbed Details View */}
              {(importProgress.successDetails?.length > 0 ||
                importProgress.errorDetails?.length > 0) && (
                <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                  <div className="flex border-b border-gray-200">
                    {importProgress.successDetails?.length > 0 && (
                      <button
                        onClick={() => setActiveResultTab("success")}
                        className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                          activeResultTab === "success"
                            ? "bg-green-50 text-green-700 border-b-2 border-green-500"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        ✅ Successful ({importProgress.successDetails.length})
                      </button>
                    )}
                    {importProgress.errorDetails?.length > 0 && (
                      <button
                        onClick={() => setActiveResultTab("errors")}
                        className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                          activeResultTab === "errors"
                            ? "bg-red-50 text-red-700 border-b-2 border-red-500"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        ❌ Failed ({importProgress.errorDetails.length})
                      </button>
                    )}
                  </div>

                  <div className="max-h-96 overflow-y-auto">
                    {/* Debug info */}
                    {process.env.NODE_ENV === "development" && (
                      <div className="p-2 bg-gray-100 text-xs text-gray-600">
                        Debug: activeTab={activeResultTab}, errors=
                        {importProgress.errorDetails?.length || 0}, success=
                        {importProgress.successDetails?.length || 0}
                      </div>
                    )}

                    {/* Success Details */}
                    {activeResultTab === "success" &&
                      importProgress.successDetails?.length > 0 && (
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Row
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Product Name
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Item Code
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                SKU
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {importProgress.successDetails.map(
                              (item, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                  <td className="px-4 py-2 text-sm text-gray-900 font-medium">
                                    {item.row}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-600">
                                    {item.productName}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-600">
                                    {item.itemCode}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-600">
                                    {item.sku}
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      )}

                    {/* Error Details */}
                    {activeResultTab === "errors" &&
                      importProgress.errorDetails?.length > 0 && (
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Row
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Product Name
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Item Code
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                SKU
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Error
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {importProgress.errorDetails.map(
                              (errorItem, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                  <td className="px-4 py-2 text-sm text-gray-900 font-medium">
                                    {errorItem.row}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-600">
                                    {errorItem.productName || "N/A"}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-600">
                                    {errorItem.itemCode || "N/A"}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-600">
                                    {errorItem.sku || "N/A"}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-red-600">
                                    {errorItem.error}
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      )}
                  </div>

                  {(importProgress.successDetails?.length >= 10 ||
                    importProgress.errorDetails?.length >= 10) && (
                    <div className="bg-yellow-50 px-4 py-2 border-t border-yellow-200">
                      <p className="text-xs text-yellow-800">
                        ⚠️ Showing first 10 items only. Check console or server
                        logs for complete details.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Success message when no errors */}
              {importProgress.errors === 0 && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <p className="text-sm text-green-700 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    All products imported successfully!
                  </p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex justify-end gap-2 pt-2">
                {importProgress.errors > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setImportProgress({
                        total: 0,
                        current: 0,
                        success: 0,
                        errors: 0,
                        errorDetails: [],
                        successDetails: [],
                      });
                      setImportFile(null);
                      setReplaceExisting(false);
                      setClearBeforeImport(false);
                      setClearOptions({ products: true, categories: false });
                      setActiveResultTab("success");
                    }}
                  >
                    Try Again
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                    setReplaceExisting(false);
                    setClearBeforeImport(false);
                    setClearOptions({ products: true, categories: false });
                    setImportProgress({
                      total: 0,
                      current: 0,
                      success: 0,
                      errors: 0,
                      errorDetails: [],
                      successDetails: [],
                    });
                    setActiveResultTab("success");
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && productToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={cancelDelete}
            ></div>

            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto overflow-hidden border border-gray-200">
              {/* Header */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">Delete Product</h2>
                      <p className="text-red-100 text-sm">
                        This action cannot be undone
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={cancelDelete}
                    className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="mb-6">
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to delete this product?
                  </p>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm font-semibold text-gray-600">
                          Product Name:
                        </span>
                        <p className="text-gray-900 font-medium">
                          {productToDelete.name}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm font-semibold text-gray-600">
                          Item Code:
                        </span>
                        <p className="text-gray-900 font-medium">
                          {productToDelete.itemCode}
                        </p>
                      </div>
                      {productToDelete.category && (
                        <div>
                          <span className="text-sm font-semibold text-gray-600">
                            Category:
                          </span>
                          <p className="text-gray-900 font-medium">
                            {productToDelete.category}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <svg
                      className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div>
                      <h4 className="text-sm font-semibold text-red-800 mb-1">
                        Warning
                      </h4>
                      <p className="text-sm text-red-700">
                        This will permanently delete the product and all
                        associated data including SKUs, variants, and inventory
                        records.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3">
                  <button
                    onClick={cancelDelete}
                    className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg font-medium hover:from-red-700 hover:to-red-800 transition-all shadow-lg hover:shadow-xl"
                  >
                    Delete Product
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
